import { Link, useParams, useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import { useRef, useState, useEffect, useContext } from "react";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";
import Notification from "../../components/UI/Notification";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { AuthContext } from "../../contexts/AuthContext";

export default function CameraCapture() {
  const { type } = useParams(); // "Clock In" or "Clock Out"
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const [time, setTime] = useState("");
  const [error, setError] = useState("");
  const [hasCameraAccess, setHasCameraAccess] = useState(true);
  const [loading, setLoading] = useState(false);
  const token = getToken();
  const [notification, setNotification] = useState(null);
  const { roles } = useContext(AuthContext);

  const getRoleBasedRouteLocal = (route) => {
    return getRoleBasedRoute(route, roles);
  };

  // update time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setTime(
        new Date().toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "numeric",
          second: "numeric",
          hour12: true,
        }),
      );
    }, 1000);

    // Validate camera permission
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        setHasCameraAccess(true);
        stream.getTracks().forEach((track) => track.stop());
      })
      .catch(() => {
        setHasCameraAccess(false);
        setError("Camera not accessible or permission denied.");
      });

    return () => clearInterval(interval); // Clean up interval on component unmount
  }, []);

  const capture = async () => {
    setError("");
    setLoading(true);

    if (!webcamRef.current) {
      setError("Camera not loaded properly.");
      setLoading(false);
      return;
    }

    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      setError("Failed to capture image.");
      setLoading(false);
      return;
    }

    try {
      const position = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject),
      );

      const { latitude, longitude } = position.coords;

      // console.log("Latitude:", {
      //   location: { latitude, longitude },
      //   selfie: imageSrc,
      //   timestamp: new Date().toISOString(),
      //   attendance_type: "face",
      // });

      const endpoint =
        type === "Clock In"
          ? "https://hrmsnew.prepstripe.com/api/attendance/checkin"
          : "https://hrmsnew.prepstripe.com/api/attendance/checkout";

      await axios.post(
        endpoint,
        {
          location: { latitude, longitude },
          selfie: imageSrc,
          timestamp: new Date().toISOString(),
          attendance_type: "face",
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // Replace token with your variable
          },
        },
      );

      navigate(
        getRoleBasedRouteLocal("/admin/attendance_management/mob-attendance/"),
      );
    } catch (err) {
      console.error(err);
      // setNotification(err.response.data.error || "Something went wrong while submitting.");
      setNotification({
        message: `${err.response.data.error || "Something went wrong while submitting."}`,
        type: "error",
      });
      setError(
        err.response.data.error || "Something went wrong while submitting.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 dark:bg-gray-900">
      {/* notification compo */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Breadcrumb */}
      <nav className="flex flex-wrap mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
          <li>
            <div className="flex items-center">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className="ms-1 text-sm sm:text-md font-medium text-gray-700 hover:text-blue-600 md:ms-2 dark:text-blue-400 dark:hover:text-blue-500"
              >
                Attendance Management
              </Link>
            </div>
          </li>
          <li>
            <div className="flex items-center">
              <svg
                className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1"
                aria-hidden="true"
                fill="none"
                viewBox="0 0 6 10"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 9 4-4-4-4"
                />
              </svg>
              <Link
                to={getRoleBasedRouteLocal(
                  "/admin/attendance_management/mob-attendance/",
                )}
                className="ms-1 text-sm sm:text-md font-medium text-gray-700 hover:text-blue-600 md:ms-2 dark:text-blue-400 dark:hover:text-blue-500"
              >
                Mobile Attendance
              </Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <svg
                className="rtl:rotate-180 w-3 h-3 text-gray-400 mx-1"
                aria-hidden="true"
                fill="none"
                viewBox="0 0 6 10"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 9 4-4-4-4"
                />
              </svg>
              <span className="ms-1 md:text-md text-sm font-medium text-gray-500 md:ms-2">
                Camera Page
              </span>
            </div>
          </li>
        </ol>
      </nav>

      {/* mobile responsive */}
      <div className="flex flex-col items-center justify-center">
        <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4 sm:p-6 space-y-6">
          <h2 className="text-xl sm:text-2xl font-bold text-center text-gray-800 dark:text-white">
            {type} - Capture Image
          </h2>

          <div className="text-2xl sm:text-3xl font-mono text-blue-600 text-center">
            {time}
          </div>

          {!hasCameraAccess ? (
            <div className="text-red-500 text-center text-sm sm:text-base">
              {error || "Camera access is required to continue."}
            </div>
          ) : (
            <>
              <div className="rounded-md overflow-hidden shadow w-full">
                <Webcam
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  className="w-full md:h-48 h-full object-cover rounded-md"
                  videoConstraints={{ facingMode: "user" }}
                />
              </div>

              {error && (
                <p className="text-red-500 text-xs sm:text-sm text-center mt-2">
                  {error}
                </p>
              )}

              {/* for desktop */}
              <button
                onClick={capture}
                disabled={loading}
                className={`w-full mt-4 py-3 px-4 text-white rounded-lg transition duration-200 hidden sm:block ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {loading ? "Submitting..." : `${type}`}
              </button>

              {/* Fixed bottom button for mobile only */}
              <div className="fixed bottom-0 left-0 right-0 sm:hidden bg-white border-t border-gray-200 p-4 z-10">
                <button
                  onClick={capture}
                  disabled={loading}
                  className={`w-full py-3 px-4 text-white rounded-lg transition duration-200 ${
                    loading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {loading ? "Submitting..." : `${type}`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
